import React, { useEffect, useState } from "react";
import { PageHeader, Card, Badge, Button, Input, Select, Spinner } from "@/components/ui";
import { endpoints, apiError, pollJob } from "@/lib/api";
import { CostTracker } from "@/components/CostTracker";
import { UserPlus, KeyRound, Users, Power, Zap } from "lucide-react";

function InviteForm({ onDone }) {
  const [f, setF] = useState({ name: "", email: "", role: "analyst", company_name: "" });
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg("");
    try {
      const r = await endpoints.adminInvite(f);
      setMsg(r.data.email_sent ? `Invitation sent to ${f.email}.` : `User created — invitation email could not be delivered.`);
      setF({ name: "", email: "", role: "analyst", company_name: "" });
      onDone();
    } catch (ex) { setMsg(apiError(ex)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={submit} className="grid md:grid-cols-5 gap-2 items-end" data-testid="admin-invite-form">
      <Input placeholder="Full name" required value={f.name} onChange={set("name")} data-testid="admin-invite-name" />
      <Input placeholder="Email" type="email" required value={f.email} onChange={set("email")} data-testid="admin-invite-email" />
      <Input placeholder="Company" value={f.company_name} onChange={set("company_name")} data-testid="admin-invite-company" />
      <Select value={f.role} onChange={set("role")} data-testid="admin-invite-role">
        <option value="advisor">Advisor</option><option value="analyst">Analyst</option>
        <option value="partner">Partner</option><option value="admin">Admin</option>
      </Select>
      <Button type="submit" disabled={busy} data-testid="admin-invite-submit"><UserPlus size={13} /> {busy ? "Inviting…" : "Invite"}</Button>
      {msg && <div className="md:col-span-5 text-[12px] text-gold" data-testid="admin-invite-msg">{msg}</div>}
    </form>
  );
}

function UserRow({ u, onChange }) {
  const [busy, setBusy] = useState(false);
  const patch = async (body) => {
    setBusy(true);
    try { await endpoints.adminPatchUser(u.user_id, body); onChange(); } catch (ex) { alert(apiError(ex)); } finally { setBusy(false); }
  };
  const us = u.usage_30d || {};
  return (
    <tr className="border-b border-white/5 text-[12.5px]" data-testid={`admin-user-row-${u.email}`}>
      <td className="py-3 pr-3"><div className="text-white">{u.name}</div><div className="text-white/40 text-[11px]">{u.email}</div></td>
      <td className="pr-3">
        <Select value={u.role} disabled={busy} onChange={(e) => patch({ role: e.target.value })} className="!py-1 !text-[12px]" data-testid={`admin-user-role-${u.email}`}>
          {["admin", "advisor", "analyst", "partner"].map((r) => <option key={r} value={r}>{r}</option>)}
        </Select>
      </td>
      <td className="pr-3">
        <Badge tone={u.is_active ? "green" : "red"}>{u.is_active ? "active" : "inactive"}</Badge>
        {!u.email_verified && <Badge tone="yellow" className="ml-1">unverified</Badge>}
      </td>
      <td className="pr-3 font-mono text-white/70" data-testid={`admin-user-usage-${u.email}`}>
        {us.companies || 0} co · {us.deals || 0} deals · {us.chat_messages || 0} msgs
        <div className="text-[10.5px] text-white/40">{us.llm_calls || 0} LLM · {us.tavily_calls || 0} web · ${(us.cost_usd || 0).toFixed(2)}</div>
      </td>
      <td className="text-right">
        <Button size="sm" variant={u.is_active ? "outline" : "primary"} disabled={busy} onClick={() => patch({ is_active: !u.is_active })} data-testid={`admin-user-toggle-${u.email}`}>
          <Power size={12} /> {u.is_active ? "Deactivate" : "Activate"}
        </Button>
      </td>
    </tr>
  );
}

function CacheWarmup() {
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => endpoints.cacheWarmupHistory().then((r) => setRuns(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const run = async () => {
    setBusy(true);
    try {
      const r = await endpoints.cacheWarmupRun();
      await pollJob(`/jobs/${r.data.job_id}`, null, 3000);
    } catch (e) { alert(apiError(e)); } finally { setBusy(false); load(); }
  };
  const last = runs[0];
  return (
    <Card hover={false} data-testid="admin-cache-warmup">
      <div className="flex items-center justify-between mb-3">
        <div className="overline flex items-center gap-2"><Zap size={12} /> Nightly cache warmup · 03:00 UTC</div>
        <Button size="sm" onClick={run} disabled={busy} data-testid="admin-cache-warmup-run">{busy ? <Spinner size={11} /> : <Zap size={11} />} {busy ? "Running… (~60s)" : "Run now"}</Button>
      </div>
      <div className="text-[11.5px] text-white/45 mb-3">Refreshes the 20 most-used web searches of the last 7 days so morning queries hit the 24h cache.</div>
      {!last ? <div className="text-[12px] text-white/40" data-testid="admin-cache-warmup-empty">No warmup has run yet.</div> : (
        <div data-testid="admin-cache-warmup-last">
          <div className="text-[12px] text-white/70 mb-2">Last run {String(last.at).slice(0, 16).replace("T", " ")} · {last.refreshed}/{last.candidates} refreshed · {last.failed} failed</div>
          <div className="max-h-40 overflow-y-auto space-y-1">
            {last.queries.map((q, i) => <div key={i} className="text-[11px] text-white/55 flex justify-between gap-2"><span className="truncate">{q.query}</span><span className="font-mono text-white/40 shrink-0">{q.hits_7d} hits</span></div>)}
          </div>
        </div>
      )}
    </Card>
  );
}

function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [draft, setDraft] = useState({});
  const load = () => endpoints.adminApiKeys().then((r) => setKeys(r.data));
  useEffect(() => { load(); }, []);
  const save = async (p, value) => {
    try { await endpoints.adminSetApiKey(p, value); setDraft({ ...draft, [p]: "" }); load(); } catch (ex) { alert(apiError(ex)); }
  };
  const remove = (p) => window.confirm(`Remove the ${LABEL[p]} API key? The provider will be skipped until a new key is saved.`) && save(p, "");
  const LABEL = { cerved: "Cerved", tavily: "Tavily", global_database: "Global Database" };
  return (
    <div className="space-y-3" data-testid="admin-api-keys">
      {keys.map((k) => (
        <div key={k.provider} className="grid md:grid-cols-[180px_1fr_auto_auto] gap-2 items-center" data-testid={`admin-key-${k.provider}`}>
          <div>
            <div className="text-[13px] text-white">{LABEL[k.provider]}</div>
            <div className="text-[10.5px] font-mono text-white/40">{k.configured ? k.masked : "not configured"}</div>
          </div>
          <Input type="password" placeholder={`New ${LABEL[k.provider]} API key`} value={draft[k.provider] || ""}
            onChange={(e) => setDraft({ ...draft, [k.provider]: e.target.value })} data-testid={`admin-key-input-${k.provider}`} />
          <Button size="sm" disabled={!(draft[k.provider] || "").trim()} onClick={() => save(k.provider, draft[k.provider].trim())} data-testid={`admin-key-save-${k.provider}`}><KeyRound size={12} /> Save</Button>
          <Button size="sm" variant="ghost" disabled={!k.configured} onClick={() => remove(k.provider)} data-testid={`admin-key-remove-${k.provider}`}>Remove</Button>
        </div>
      ))}
    </div>
  );
}

export default function Admin() {
  const [users, setUsers] = useState(null);
  const load = () => endpoints.adminUsers().then((r) => setUsers(r.data)).catch(() => setUsers([]));
  useEffect(() => { load(); }, []);
  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6">
      <PageHeader eyebrow="Administration" title={<>Admin <span className="italic text-gold">Panel</span></>} description="Manage users, roles, usage and provider API keys." testid="admin-page" />
      <Card hover={false}>
        <div className="overline mb-4 flex items-center gap-2"><UserPlus size={12} /> Invite a user</div>
        <InviteForm onDone={load} />
      </Card>
      <Card hover={false}>
        <div className="overline mb-4 flex items-center gap-2"><Users size={12} /> Users & usage (30 days)</div>
        {!users ? <Spinner /> : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="admin-users-table">
              <thead><tr className="text-left text-[10px] uppercase tracking-[0.14em] text-white/40 border-b border-white/10">
                <th className="py-2">User</th><th>Role</th><th>Status</th><th>Usage</th><th />
              </tr></thead>
              <tbody>{users.map((u) => <UserRow key={u.user_id} u={u} onChange={load} />)}</tbody>
            </table>
          </div>
        )}
      </Card>
      <div className="grid lg:grid-cols-2 gap-6">
        <Card hover={false}><div className="overline mb-4 flex items-center gap-2"><KeyRound size={12} /> Provider API keys</div><ApiKeys /></Card>
        <CostTracker scope="all" title="Platform cost tracker" />
      </div>
      <CacheWarmup />
    </div>
  );
}
